import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import {
    type ApiDossiersIdActionPutActionEnum,
    type ApiDossiersIdTransitionsGetCollection200ResponseHydraMemberInner,
    type StatutJsonldStatutRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';
import useTransition from '../../../hooks/useTransition';
import {
    REFRESH_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
} from '../../../providers/CurrentDossierProvider';
import StatusChip from './StatusChip';

type TimelineWithValidatorsStepProps = {
    id: number;
    label: string;
    icon?: IconName;
    current: boolean;
    onClick?: () => void;
};

type TimelineWithValidatorsProps = {
    steps: Array<TimelineWithValidatorsStepProps>;
    showAllStatus?: boolean;
};

const TimeLineWithValidators: React.FunctionComponent<TimelineWithValidatorsProps> = function ({
    steps,
    showAllStatus = true,
}) {
    const { palette, breakpoints } = useTheme();
    const isMobile = useMediaQuery(breakpoints.down('md'));
    const { statusApi } = useFormaliteService();
    const { dossier } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();
    const { applyTransition, canApplyTransition, getTransitionStatusAvailable } = useTransition();
    const { enqueueSnackbar } = useSnackbar();
    const { modalActions } = useModal();

    const [allStatuses, setAllStatuses] = useState<StatutJsonldStatutRead[]>([]);
    const [availableTransitions, setAvailableTransitions] = useState<
        ApiDossiersIdTransitionsGetCollection200ResponseHydraMemberInner[]
    >([]);
    const [isApplying, setIsApplying] = useState<number | null>(null);

    useEffect(() => {
        statusApi
            .apiStatutsGetCollection({ itemsPerPage: 50, type: 1 })
            .then((res) =>
                setAllStatuses(res.data['hydra:member'].filter((status) => status.step) || []),
            )
            .catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!dossier) return;
        getTransitionStatusAvailable(dossier)
            .then((res) => setAvailableTransitions(res['hydra:member'] || []))
            .catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier?.statutId]);

    const findTransitionForStatus = useCallback(
        (statusId: number) => availableTransitions.find((t) => t.target?.id === statusId),
        [availableTransitions],
    );

    const handleStatusClick = useCallback(
        async (statusId: number, identifier: string) => {
            if (!dossier) return;

            const transition = identifier as ApiDossiersIdActionPutActionEnum;
            if (!canApplyTransition(dossier, transition)) return;
            setIsApplying(statusId);
            try {
                await applyTransition(dossier, transition);
                enqueueSnackbar('Statut mis à jour', { variant: 'success' });
                dispatch?.({ type: REFRESH_CURRENT_DOSSIER_CTX });
            } catch {
                enqueueSnackbar('Erreur lors de la mise à jour du statut', { variant: 'error' });
            } finally {
                setIsApplying(null);
            }
        },
        [dossier, canApplyTransition, applyTransition, dispatch, enqueueSnackbar],
    );

    // ── Timeline geometry ────────────────────────────────────────────────────

    let currentDone = false;
    const currentIndex = steps.reduce((acc, step) => {
        currentDone = currentDone || step.current;
        return !currentDone || step.current ? acc + 1 : acc;
    }, -1);

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    position: 'relative',
                    zIndex: 0,
                }}
                data-testid="timeline-with-validators"
            >
                {steps.map((step, index) => {
                    const isFirst = index === 0;
                    const isLast = index === steps.length - 1;
                    const stepStatuses = allStatuses.filter((s) => s.step!.id === step.id);

                    let stepAlignItems = 'center';
                    if (isFirst) stepAlignItems = 'flex-start';
                    if (isLast) stepAlignItems = 'flex-end';

                    const beforeWidth = isLast ? 'calc(100% - 12px)' : 'calc(50% - 12px)';
                    const beforeHeight = isLast ? 'calc(100% - 9px)' : 'calc(50% - 9px)';
                    const afterWidth = isFirst ? 'calc(100% - 12px)' : 'calc(50% - 12px)';
                    const afterTop = isFirst ? 0 : '50%';
                    const afterHeight = isFirst ? '100%' : '50%';

                    const sharedLine = {
                        content: '""',
                        position: 'absolute',
                        zIndex: 1,
                        height: '2px',
                    };

                    return (
                        <Box
                            key={`step-${step.id}`}
                            sx={{
                                display: 'flex',
                                flexDirection: isMobile ? 'row' : 'column',
                                alignItems: stepAlignItems,
                                position: 'relative',
                                flexGrow: 1,
                                textAlign: 'center',
                                gap: isMobile ? '0 10px' : '10px 0',
                                padding: isMobile ? '30px 0' : 0,
                                cursor: step.onClick ? 'pointer' : 'auto',
                                color: palette.grey[500],
                                '&:first-of-type': { paddingTop: 0 },
                                '&:last-child': { paddingBottom: 0 },
                                '&:hover': {
                                    color: step.onClick ? palette.primary.main : palette.grey[500],
                                },
                                '&:before': {
                                    ...sharedLine,
                                    display: index > 0 ? 'block' : 'none',
                                    left: isMobile ? '8px' : 0,
                                    top: isMobile ? 0 : '8px',
                                    width: isMobile ? '2px' : beforeWidth,
                                    height: isMobile ? beforeHeight : '2px',
                                    backgroundColor:
                                        currentIndex >= index
                                            ? palette.primary.main
                                            : palette.icon?.primary?.backgroundColor,
                                    borderTopRightRadius: '1px',
                                    borderBottomRightRadius: '1px',
                                },
                                '&:after': {
                                    ...sharedLine,
                                    display: index < steps.length - 1 ? 'block' : 'none',
                                    left: isMobile ? '8px' : 'auto',
                                    right: isMobile ? 'auto' : 0,
                                    top: isMobile ? afterTop : '8px',
                                    width: isMobile ? '2px' : afterWidth,
                                    height: isMobile ? afterHeight : '2px',
                                    backgroundColor:
                                        currentIndex > index
                                            ? palette.primary.main
                                            : palette.icon?.primary?.backgroundColor,
                                    borderTopLeftRadius: '1px',
                                    borderBottomLeftRadius: '1px',
                                },
                            }}
                            onClick={step.onClick}
                        >
                            {/* Dot */}
                            <Box
                                sx={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '6px',
                                    zIndex: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor:
                                        currentIndex >= index
                                            ? palette.primary.main
                                            : palette.icon?.primary?.backgroundColor,
                                }}
                            >
                                {step.icon && (
                                    <FaOptionIcon
                                        icon={['fasdl', step.icon]}
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '6px',
                                            lineHeight: '14.4px',
                                            color: palette.background.paper,
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Label */}
                            <Typography
                                component="span"
                                sx={{
                                    fontWeight: 500,
                                    fontSize: isMobile ? '14px' : '12px',
                                    lineHeight: '14.4px',
                                    cursor: step.onClick ? 'pointer' : 'auto',
                                    transition: 'color 0.225s ease-out',
                                }}
                            >
                                {step.label}
                            </Typography>

                            {/* Status chips */}
                            {stepStatuses.length > 0 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px',
                                        marginTop: '6px',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {stepStatuses
                                        .filter((status) =>
                                            showAllStatus
                                                ? true
                                                : !!findTransitionForStatus(status.id as number) ||
                                                  status.id === dossier?.statutId,
                                        )
                                        .map((status) => {
                                            const transition = findTransitionForStatus(
                                                status.id as number,
                                            );
                                            return (
                                                <StatusChip
                                                    key={status.id}
                                                    status={status}
                                                    isCurrent={status.id === dossier?.statutId}
                                                    isClickable={!!transition}
                                                    isProcessing={isApplying === status.id}
                                                    onTransitionClick={
                                                        transition
                                                            ? () => {
                                                                  modalActions.call(
                                                                      <ConfirmationModal
                                                                          isFullWidth
                                                                          color={palette.primary}
                                                                          messages={{
                                                                              question:
                                                                                  'Souhaitez-vous modifier le statut de ce dossier ?',
                                                                              detail: {
                                                                                  message:
                                                                                      'La modification du statut impactera la progression du dossier.',
                                                                                  severity: 'info',
                                                                              },
                                                                              validation:
                                                                                  'Confirmer la modification',
                                                                          }}
                                                                          actionOnValidation={() => {
                                                                              handleStatusClick(
                                                                                  status.id as number,
                                                                                  transition.identifier as string,
                                                                              );
                                                                              modalActions.close();
                                                                          }}
                                                                          actionOnCancellation={() =>
                                                                              modalActions.close()
                                                                          }
                                                                      />,
                                                                  );
                                                              }
                                                            : undefined
                                                    }
                                                />
                                            );
                                        })}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default TimeLineWithValidators;
